/** Demo telephony — simulates the media layer so the softphone + CRM screen-pop
 *  + call-logging flow are fully testable without a real trunk. Outbound rings
 *  then connects; inbound can be triggered from Settings ("Simulate inbound") or
 *  the sample CRM. This is the default provider until a real edge is configured. */
import type { Contact, TelephonyEvents, TelephonyProvider } from "./types";

let seq = 0;
const newId = () => `demo-${Date.now().toString(36)}-${seq++}`;

export class DemoTelephonyProvider implements TelephonyProvider {
  readonly name = "demo";
  private ev!: TelephonyEvents;
  private timers = new Map<string, number[]>();
  private answered = new Set<string>();

  async init(events: TelephonyEvents): Promise<void> {
    this.ev = events;
  }

  async dial(contact: Contact): Promise<void> {
    const callId = newId();
    this.ev.onOutgoing(contact, callId);
    this.schedule(callId, [
      [800, () => this.ev.onRinging(callId)],
      [3000, () => { this.answered.add(callId); this.ev.onConnected(callId); }],
    ]);
  }

  async answer(callId: string): Promise<void> {
    this.clear(callId);
    this.answered.add(callId);
    this.ev.onConnected(callId);
  }

  async hangup(callId: string): Promise<void> {
    this.clear(callId);
    const outcome = this.answered.has(callId) ? "completed" : "abandoned";
    this.answered.delete(callId);
    this.ev.onEnded(callId, outcome);
  }

  async hold(): Promise<void> { /* no-op in demo */ }
  async mute(): Promise<void> { /* no-op in demo */ }
  async sendDigit(): Promise<void> { /* no-op in demo */ }
  async setRecording(): Promise<void> { /* no-op in demo */ }
  async transfer(callId: string, _target: string, _kind: string, mode: "blind" | "warm"): Promise<void> {
    // Blind transfer hands the call off entirely → the agent's leg ends; warm
    // stays bridged (nothing to simulate locally).
    if (mode === "blind") { this.clear(callId); this.answered.delete(callId); this.ev.onEnded(callId, "completed"); }
  }
  async conference(): Promise<void> { /* no-op in demo */ }

  simulateInbound(contact: Contact): void {
    const callId = newId();
    this.ev.onIncoming(contact, callId);
    // Unanswered inbound rings out after 20s → missed.
    this.schedule(callId, [[20000, () => { if (!this.answered.has(callId)) this.ev.onEnded(callId, "missed"); }]]);
  }

  dispose(): void {
    this.timers.forEach((ts) => ts.forEach((t) => clearTimeout(t)));
    this.timers.clear();
    this.answered.clear();
  }

  private schedule(callId: string, steps: [number, () => void][]): void {
    const handles = steps.map(([ms, fn]) => window.setTimeout(fn, ms));
    this.timers.set(callId, [...(this.timers.get(callId) ?? []), ...handles]);
  }

  private clear(callId: string): void {
    (this.timers.get(callId) ?? []).forEach((t) => clearTimeout(t));
    this.timers.delete(callId);
  }
}
